import { PAY_STATUS_CONFIG, REG_STATUS_CONFIG } from '@/lib/status-config';
import { getTeamSportingCaptain, getTeamSportingCaptainEmail } from '@/lib/team-sporting-captain';
import { formatDateAbbreviatedFr } from '@shared';
import { TeamRegistrationStatus } from '@shared/enums/domain.enum';
import type { Team } from '@shared/types/entities';
import { Calendar, CheckCircle, Clock, Edit, Phone, Trash2, X, XCircle } from 'lucide-react';

function initials(first?: string | null, last?: string | null) {
  return `${(first ?? '').charAt(0)}${(last ?? '').charAt(0)}`.toUpperCase() || '?';
}

interface TeamDetailModalProps {
  team: Team | null;
  onClose: () => void;
  onEdit: (team: Team) => void;
  onDelete: (team: Team) => void;
}

export default function TeamDetailModal({ team, onClose, onEdit, onDelete }: TeamDetailModalProps) {
  if (!team) return null;

  const regCfg = REG_STATUS_CONFIG[team.registrationStatus];
  const payCfg = PAY_STATUS_CONFIG[team.paymentStatus];

  return (
    <div className='fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4'>
      <div className='absolute inset-0 bg-black/50 backdrop-blur-sm' onClick={onClose} />

      <div className='relative z-10 w-full sm:max-w-lg max-h-[90vh] overflow-y-auto bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl'>
        {/* Header */}
        <div className='sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between rounded-t-2xl'>
          <div className='min-w-0 pr-4'>
            <h2 className='text-lg font-bold text-slate-900 truncate'>{team.name}</h2>
            {team.competition && <p className='text-sm text-slate-500 mt-0.5 truncate'>{team.competition.name}</p>}
          </div>
          <button
            onClick={onClose}
            aria-label='Fermer'
            className='p-2 rounded-lg hover:bg-slate-100 transition-colors shrink-0'
          >
            <X className='w-5 h-5 text-slate-500' />
          </button>
        </div>

        <div className='p-6 space-y-5'>
          {/* Status badges */}
          <div className='flex flex-wrap gap-2'>
            <span
              className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium ${regCfg.bg} ${regCfg.text}`}
            >
              {team.registrationStatus === TeamRegistrationStatus.CONFIRMED ? (
                <CheckCircle className='w-3.5 h-3.5' />
              ) : team.registrationStatus === TeamRegistrationStatus.PENDING ? (
                <Clock className='w-3.5 h-3.5' />
              ) : (
                <XCircle className='w-3.5 h-3.5' />
              )}
              {regCfg.label}
            </span>
            <span
              className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${payCfg.bg} ${payCfg.text}`}
            >
              {payCfg.label}
            </span>
            {team.paidAmount != null && team.paidAmount > 0 && (
              <span className='inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-700'>
                {(team.paidAmount / 100).toFixed(2)} €
              </span>
            )}
          </div>

          {/* Competition */}
          {team.competition && (
            <div className='bg-slate-50 rounded-xl p-4'>
              <p className='text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2'>Concours</p>
              <p className='font-semibold text-slate-900'>{team.competition.name}</p>
              <div className='flex items-center gap-4 mt-1.5 text-xs text-slate-500'>
                <span className='flex items-center gap-1'>
                  <Calendar className='w-3.5 h-3.5' />
                  {formatDateAbbreviatedFr(team.competition.date)}
                </span>
                {team.competition.city && <span>{team.competition.city}</span>}
              </div>
            </div>
          )}

          {/* Captain */}
          {(getTeamSportingCaptain(team) || team.captainPhone) && (
            <div className='bg-slate-50 rounded-xl p-4'>
              <p className='text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3'>Capitaine</p>
              <div className='flex items-center gap-3'>
                <div className='w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center shrink-0'>
                  <span className='text-sm font-bold text-emerald-700'>
                    {initials(getTeamSportingCaptain(team)?.firstName, getTeamSportingCaptain(team)?.lastName)}
                  </span>
                </div>
                <div className='min-w-0'>
                  {getTeamSportingCaptain(team) && (
                    <p className='font-semibold text-slate-900 truncate'>
                      {getTeamSportingCaptain(team)?.firstName} {getTeamSportingCaptain(team)?.lastName}
                    </p>
                  )}
                  {getTeamSportingCaptainEmail(team) && (
                    <p className='text-sm text-slate-500 truncate'>{getTeamSportingCaptainEmail(team)}</p>
                  )}
                  {team.captainPhone && (
                    <p className='text-sm text-slate-500 flex items-center gap-1 mt-0.5'>
                      <Phone className='w-3.5 h-3.5 shrink-0' />
                      {team.captainPhone}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Players */}
          {team.players && team.players.length > 0 && (
            <div>
              <p className='text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3'>
                Joueurs ({team.players.length})
              </p>
              <div className='space-y-2'>
                {[...team.players]
                  .sort((a, b) => a.playerOrder - b.playerOrder)
                  .map(player => (
                    <div key={player.id} className='flex items-center gap-3 p-3 rounded-xl bg-slate-50'>
                      <div className='w-7 h-7 rounded-full bg-slate-200 flex items-center justify-center shrink-0'>
                        <span className='text-xs font-bold text-slate-600'>{player.playerOrder}</span>
                      </div>
                      <div className='flex-1 min-w-0'>
                        <p className='font-medium text-slate-900 truncate'>
                          {player.firstName} {player.lastName}
                        </p>
                        {player.licenseNumber && (
                          <p className='text-xs text-slate-400 font-mono'>{player.licenseNumber}</p>
                        )}
                        {player.licenseAutoDeclaredAt && (
                          <p className='text-xs text-emerald-600 font-medium'>
                            Déclarée sur l&apos;honneur le {formatDateAbbreviatedFr(player.licenseAutoDeclaredAt)}
                          </p>
                        )}
                      </div>
                      {player.playerOrder === 1 && (
                        <span className='text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full font-medium shrink-0'>
                          Cap.
                        </span>
                      )}
                    </div>
                  ))}
              </div>
            </div>
          )}

          {/* Notes */}
          {team.notes && (
            <div className='bg-amber-50 rounded-xl p-4'>
              <p className='text-xs font-semibold text-amber-700 mb-1'>Notes</p>
              <p className='text-sm text-amber-900'>{team.notes}</p>
            </div>
          )}

          <p className='text-xs text-slate-400'>Inscrite le {formatDateAbbreviatedFr(team.createdAt)}</p>
        </div>

        {/* Footer actions */}
        <div className='sticky bottom-0 bg-white border-t border-slate-200 px-6 py-4 flex gap-3'>
          <button
            onClick={() => {
              onClose();
              onEdit(team);
            }}
            className='flex-1 flex items-center justify-center gap-2 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-sm font-semibold transition-colors'
          >
            <Edit className='w-4 h-4' />
            Modifier le nom
          </button>
          <button
            onClick={() => {
              onClose();
              onDelete(team);
            }}
            className='flex items-center justify-center gap-2 px-4 py-2.5 border border-red-200 hover:bg-red-50 text-red-600 rounded-xl text-sm font-semibold transition-colors'
          >
            <Trash2 className='w-4 h-4' />
            Supprimer
          </button>
        </div>
      </div>
    </div>
  );
}
